import { useMemo } from "react";
import { useSlot } from "src/components/Slots/SlotsContext";
import { hasCategories, resolveFilters } from "src/helpers/searchFiltersUrl";
import { useGetArticlesCategories } from "src/hooks/useArticles";
import { useArticlesSearch } from "src/hooks/useArticlesSearch";
import { useSearchFiltersState } from "src/hooks/useSearchFiltersState";
import { useSearchPage } from "src/hooks/useSearchPage";

import { ArticlesViews } from "./ArticlesViews/ArticlesViews";
import { DefaultSearch } from "./DefaultSearch";
import { ArticlesFilters } from "./Filters/ArticlesFilters/ArticlesFilters";
import { SearchEmptyState } from "./SearchEmptyState";

const PAGE_SIZE = 20;

const defaultDetails = {
	departments: [],
	tags: [],
	subtags: [],
	searchValue: "",
};

type ArticlesDetails = Record<string, unknown> & typeof defaultDetails;

// wyszukiwanie i filtry działów/tagów po stronie backendu (v2 /search?type=articles)
export default function ArticlesSearch() {
	const [page, setPage] = useSearchPage();
	const [urlDetails, setDetails] =
		useSearchFiltersState<ArticlesDetails>(defaultDetails);
	const { data: tree } = useGetArticlesCategories();
	const details = useMemo(
		() => resolveFilters(urlDetails, { tree }) as ArticlesDetails,
		[urlDetails, tree],
	);
	const isReady = !!tree || !hasCategories(details);
	const { data, isLoading } = useArticlesSearch(isReady ? details : null, page);
	const ActionsSlot = useSlot("articlesSearchActions");

	const handleSetDetails = (value: Record<string, unknown>) => {
		setPage(0);
		setDetails(value);
	};

	return (
		<DefaultSearch
			tabsActions={ActionsSlot && <ActionsSlot />}
			renderView={({ scrollToTop, clearFilters }) => (
				<ArticlesViews
					// wyłączone zapytanie (czeka na drzewo działów) = brak danych, nie pusta lista
					isLoading={isLoading || !data}
					articles={data?.items ?? []}
					searchValue={details.searchValue}
					pagesCount={Math.ceil((data?.count ?? 0) / PAGE_SIZE)}
					page={page}
					setPage={setPage}
					scrollToTop={scrollToTop}
					emptyState={
						<SearchEmptyState
							type="articles"
							searchValue={details.searchValue}
							onClearFilters={clearFilters}
						/>
					}
				/>
			)}
			details={details}
			setDetails={handleSetDetails}
			filters={(draft, setDraft) => (
				<ArticlesFilters details={draft} setDetails={setDraft} />
			)}
			defaultDetails={defaultDetails}
			count={data?.count}
			counts={data?.counts}
			activeFilter="articles"
		/>
	);
}
